import { useCallback, useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import {
    sameTimeEntryPerson,
    timeEntriesOverlap,
    clampTimeEntryType,
    type TimeEntryDocType,
    type UserDocType,
    type AccountResolver,
    type MatchableAccount
} from '@ast-data/shared';
import { withoutLoginNames } from '../users/personNameSuggestions';
import { type TimeEntryVariant } from './timeEntryVariant';

import { compareNatural } from '../../utils/compare';

function sortedNames(names: readonly string[]): string[] {
    return [...new Set(names.filter(Boolean))].sort((firstName, secondName) =>
        compareNatural(firstName, secondName)
    );
}

/**
 * Name suggestions for a Zeiterfassung's name column: every name already
 * recorded in that variant ∪, for worker entries only, the user accounts. Only
 * autocomplete options — free text stays allowed, which is what lets a Gerät be
 * named once and picked from the list afterwards.
 *
 * The entries are read here rather than passed in so every view gets the SAME
 * suggestions, the field day grid included.
 *
 * `nameOf` (from useUserLabel) must be the SAME resolver the save path matches
 * on, or a picked suggestion does not link back to its account.
 */
export function useTimeEntryNameOptions(
    variant: TimeEntryVariant,
    users: readonly UserDocType[],
    nameOf: (user: UserDocType) => string
): string[] {
    const entriesQuery = useStableQuery<TimeEntryDocType>({
        selector: { entryType: variant.type }
    });
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );
    const linkUsers = variant.linkUsers;

    return useMemo(
        () =>
            sortedNames([
                // A name that is an account's LOGIN name is the same person
                // written worse; offering it again spreads a second spelling.
                ...withoutLoginNames(
                    entries.map((entry) => entry.workerName),
                    users,
                    nameOf
                ),
                // A Gerät is not a person — no account names for it.
                ...(linkUsers ? users.map((user) => nameOf(user)) : [])
            ]),
        [entries, users, nameOf, linkUsers]
    );
}

/**
 * The names the FORM DIALOG offers — the grids' suggestions, except where the
 * variant picks its names from the accounts (Mitarbeiter): there the list IS
 * the accounts, because the field takes no free text and a typo must not be
 * able to invent a second person.
 *
 * `current` — the name(s) the form holds — joins that list even when it
 * belongs to no (longer) active or employed account: a row booked before the
 * field became a picker still shows what it says instead of opening blank,
 * and a name picked before the date changed stays visible as picked.
 */
export function useTimeEntryFormNameOptions(
    variant: TimeEntryVariant,
    users: readonly UserDocType[],
    nameOf: (user: UserDocType) => string,
    current: string | readonly string[] = ''
): string[] {
    const recorded = useTimeEntryNameOptions(variant, users, nameOf);
    const fromAccounts = variant.namesFromAccounts;

    return useMemo(() => {
        if (!fromAccounts) {
            return recorded;
        }
        return sortedNames([
            ...users.map((user) => nameOf(user)),
            ...(typeof current === 'string' ? [current] : current).map((name) =>
                `${name ?? ''}`.trim()
            )
        ]);
    }, [fromAccounts, recorded, users, nameOf, current]);
}

export interface TimeEntryDayBooking {
    /** The spans already booked that day, "07:00–16:00" each. */
    spans: string[];
    /** Whether one of them overlaps the times the form would book. */
    conflicts: boolean;
}

/** Shared, so an unbooked name costs no object per option. */
const NO_BOOKING: TimeEntryDayBooking = { spans: [], conflicts: false };

/**
 * What a name is ALREADY booked for on a given day, and whether that stands in
 * the way of the times currently in the form.
 *
 * Booking a crew means picking a dozen names at once; without this the
 * double-booking only surfaces when Speichern refuses it, long after the pick.
 * So the bracket behind each option says what that person's day holds ("Max
 * Mustermann (07:00–16:00)"), and a colliding option cannot be picked at all.
 *
 * Spans ALL projects — being booked on another Maßnahme is exactly what has to
 * show — and is decided by the save-time rule itself (`timeEntriesOverlap` over
 * `sameTimeEntryPerson`), so picker and refusal cannot disagree about who
 * someone is or what counts as a clash.
 */
export function useTimeEntryDayBookings(
    variant: TimeEntryVariant,
    date: string,
    /** The times the form would book. Half a span clashes with nothing yet. */
    startTime: string,
    endTime: string,
    resolveAccount: AccountResolver<MatchableAccount>,
    /** The entry being edited — its own times are not news about itself. */
    excludeId = ''
): (name: string) => TimeEntryDayBooking {
    const entriesQuery = useStableQuery<TimeEntryDocType>({
        selector: { date }
    });
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useMemo(
        () => entriesDocs.map((doc) => doc.toMutableJSON() as TimeEntryDocType),
        [entriesDocs]
    );
    const linkUsers = variant.linkUsers;
    const type = variant.type;

    /**
     * The day's bookable entries, earliest first — WHOSE they are is decided per
     * name below. Grouping by a person key up front misses what the key cannot
     * express: an entry written before its worker's account link existed is
     * filed under the name, the same person's linked entry under the account.
     */
    const dayEntries = useMemo(
        () =>
            entries
                .filter(
                    (entry) =>
                        entry.id !== excludeId &&
                        clampTimeEntryType(entry.entryType) === type &&
                        !!entry.startTime &&
                        !!entry.endTime
                )
                .sort((firstEntry, secondEntry) =>
                    firstEntry.startTime.localeCompare(secondEntry.startTime)
                ),
        [entries, excludeId, type]
    );

    return useCallback(
        (name: string) => {
            // Looked up the way it would be SAVED, so a person booked under a
            // differently spelled name still counts as the same person.
            const userId = linkUsers ? (resolveAccount(name)?.id ?? '') : '';
            const booked = dayEntries.filter((entry) =>
                sameTimeEntryPerson({ userId, workerName: name }, entry)
            );
            if (booked.length === 0) {
                return NO_BOOKING;
            }
            const candidate = {
                entryType: type,
                userId,
                workerName: name,
                date,
                startTime,
                endTime
            };
            return {
                spans: booked.map(
                    (entry) => `${entry.startTime}–${entry.endTime}`
                ),
                conflicts:
                    !!startTime &&
                    !!endTime &&
                    booked.some((entry) => timeEntriesOverlap(candidate, entry))
            };
        },
        [dayEntries, linkUsers, resolveAccount, type, date, startTime, endTime]
    );
}
